import { WarningAmberOutlined } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  useTheme,
  Tooltip,
  TableSortLabel,
  IconButton,
  Collapse,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import GetAppIcon from "@mui/icons-material/GetApp";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import DescriptionIcon from "@mui/icons-material/Description";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import TableViewIcon from "@mui/icons-material/TableView";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import axios from "../../utils/axiosInstance";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import EmailInfoNotification from "../currentAlarm/InfoNotificationEmailOnly";
import RecipientInfoNotification from "../currentAlarm/InfoNotificationRecipientsOnly";
import { formatDate, formatReadableDate } from "../../utils/formatDate";

export default function AlarmHistory() {
  const theme = useTheme();
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [filteredNotifications, setFilteredNotifications] = useState([]);
  const [notificationsQuery, setNotificationsQuery] = useState("");
  const [notificationsPage, setNotificationsPage] = useState(0);
  const [notificationsRowsPerPage, setNotificationsRowsPerPage] = useState(10);
  const [notificationExportMenuAnchor, setNotificationExportMenuAnchor] =
    useState(null);
  const [showEmailInfo, setShowEmailInfo] = useState(false);
  const [showPhoneInfo, setShowPhoneInfo] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [expandedRows, setExpandedRows] = useState({});

  const toggleRowExpansion = (rowId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  };

  const fetchNotifications = async () => {
    try {
      setLoadingNotifications(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_GATEAWAY}/iot/alarms/alarmLogs`
      );
      let filteredData = response.data.filter((notif) =>
        (notif.notification || "").includes("Critical Alarm")
      );
      setNotifications(filteredData);
      setFilteredNotifications(filteredData);
      setLoadingNotifications(false);
    } catch (err) {
      console.error("Error fetching alarm notifications:", err);
      setLoadingNotifications(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const handleNotificationExportMenuOpen = (event) => {
    setNotificationExportMenuAnchor(event.currentTarget);
  };

  const handleNotificationExportMenuClose = () => {
    setNotificationExportMenuAnchor(null);
  };

  const handleNotificationsSearch = (e) => {
    const q = e.target.value.toLowerCase();
    setNotificationsQuery(q);
    const filtered = notifications.filter((notif) => {
      return (
        (notif.notification || "").toLowerCase().includes(q) ||
        (notif.recipients || "").toLowerCase().includes(q) ||
        (notif.time || "").toLowerCase().includes(q)
      );
    });
    setFilteredNotifications(filtered);
    setNotificationsPage(0);
  };

  const handleNotificationsChangePage = (event, newPage) => {
    setNotificationsPage(newPage);
  };

  const handleNotificationsChangeRowsPerPage = (event) => {
    setNotificationsRowsPerPage(parseInt(event.target.value, 10));
    setNotificationsPage(0);
  };

  const openEmailInfo = (notificationData) => {
    setSelectedNotification(notificationData);
    setShowEmailInfo(true);
  };

  const openPhoneInfo = (notificationData) => {
    setSelectedNotification(notificationData);
    setShowPhoneInfo(true);
  };

  const copyToClipboard = () => {
    const textContent = sortedNotifications
      .map((notif) => {
        return `${notif.time}\t${notif.unitId}\t${notif.notification}\t${notif.emailsLength}\t${notif.recipientsLength}`;
      })
      .join("\n");

    const headers = "Date\tUnitId\tAlarm\tEmail List\tPhone List";

    navigator.clipboard
      .writeText(`${headers}\n${textContent}`)
      .then(() => console.log(`Notifications data copied to clipboard`))
      .catch((err) => console.error("Failed to copy:", err));

    handleNotificationExportMenuClose();
    toast.success("Copied alarm history to clipboard");
  };

  const exportToCSV = () => {
    const csvContent = [
      ["Date", "UnitId", "Alarm", "Email List", "Phone List"],
      ...sortedNotifications.map((notif) => [
        notif.time,
        notif.unitId,
        notif.notification,
        notif.emailsLength,
        notif.recipientsLength,
      ]),
    ];

    const csvString = csvContent
      .map((row) => row.map((field) => `"${field}"`).join(","))
      .join("\n");

    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `Alarm_History_${
      new Date().toISOString().split("T")[0]
    }.csv`;
    link.click();

    handleNotificationExportMenuClose();
    toast.success("Exported Alarm History to CSV");
  };

  const exportToExcel = () => {
    const headers = ["Date", "UnitId", "Alarm", "Email List", "Phone List"];
    const rows = sortedNotifications.map((notif) => [
      notif.time,
      notif.unitId,
      notif.notification,
      notif.emailsLength,
      notif.recipientsLength,
    ]);

    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Alarm History");
    XLSX.writeFile(
      workbook,
      `Alarm_History_${new Date().toISOString().split("T")[0]}.xlsx`
    );

    handleNotificationExportMenuClose();
    toast.success(`Exported Alarm History to Excel`);
  };

  const exportToPDF = () => {
    const doc = new jsPDF("l", "pt", "a4");
    doc.setFontSize(12);
    doc.text("Alarm History Report", 40, 30);

    const headers = [["Date", "UnitId", "Alarm", "Email List", "Phone List"]];
    const rows = sortedNotifications.map((notif) => [
      notif.time,
      notif.unitId,
      notif.notification,
      notif.emailsLength,
      notif.recipientsLength,
    ]);

    autoTable(doc, {
      head: headers,
      body: rows,
      startY: 50,
      theme: "grid",
      styles: {
        fontSize: 8,
        cellPadding: 3,
        overflow: "linebreak",
      },
      headStyles: { fillColor: [211, 47, 47] },
      tableWidth: "auto",
      columnStyles: {
        0: { cellWidth: 120 },
        1: { cellWidth: 80 },
        2: { cellWidth: 300 },
        3: { cellWidth: 100 },
        4: { cellWidth: 100 },
      },
    });

    doc.save(`Alarm_History_${new Date().toISOString().split("T")[0]}.pdf`);
    handleNotificationExportMenuClose();
    toast.success(`Exported Alarm History to PDF`);
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const sortNotifications = (notificationsToSort) => {
    if (!sortConfig.key) return notificationsToSort;

    return [...notificationsToSort].sort((a, b) => {
      const aValue = a[sortConfig.key] || "";
      const bValue = b[sortConfig.key] || "";

      if (typeof aValue === "string") {
        return sortConfig.direction === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      } else if (typeof aValue === "number") {
        return sortConfig.direction === "asc"
          ? aValue - bValue
          : bValue - aValue;
      } else {
        return 0;
      }
    });
  };

  const sortedNotifications = sortNotifications(filteredNotifications);
  const paginatedNotifications = sortedNotifications.slice(
    notificationsPage * notificationsRowsPerPage,
    notificationsPage * notificationsRowsPerPage + notificationsRowsPerPage
  );

  return (
    <Grid item xs={12} md sx={{ height: "100%", width: "100%" }}>
      <Paper
        elevation={4}
        sx={{
          p: 1.5,
          display: "flex",
          flexDirection: "column",
          height: "100%",
          width: "100%",
          position: "relative",
        }}
      >
        {loadingNotifications && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              bgcolor: "rgba(187, 183, 183, 0.12)",
              backdropFilter: "blur(10px)",
              zIndex: 10,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress color="secondary" size={70} />
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              fontSize={{ xs: "1rem", sm: "1.25rem", md: "1.5rem" }}
              fontWeight="bold"
            >
              Alarm History: Critical Alarm Notifications
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              color="white"
              sx={{ borderRadius: 0 }}
              startIcon={<GetAppIcon />}
              onClick={handleNotificationExportMenuOpen}
            >
              Export
            </Button>
          </Box>
        </Box>

        <TextField
          label="Search:"
          variant="outlined"
          size="small"
          value={notificationsQuery}
          onChange={handleNotificationsSearch}
          fullWidth
          sx={{ mb: 2 }}
        />

        <TableContainer
          sx={{
            flexGrow: 1,
            height: { xs: "50vh", md: "59.7vh" },
            overflow: "auto",
            WebkitOverflowScrolling: "touch",
            overscrollBehaviorY: "none",
          }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: theme.palette.grey[100] }}>
                {/* Mobile expand button column */}
                <TableCell
                  sx={{
                    display: { xs: "table-cell", md: "none" },
                    fontWeight: "bold",
                    textAlign: "center",
                    fontSize: "12px",
                    padding: "12px 8px",
                    width: "50px",
                    color: "white",
                    backgroundColor: "#000",
                  }}
                >
                  Details
                </TableCell>

                <TableCell
                  sx={{
                    textAlign: "center",
                    fontWeight: "bold",
                    cursor: "pointer",
                    color: "white",
                    backgroundColor: "#000",
                    fontSize: { xs: "11px", sm: "14px", md: "15px" },
                    padding: "8px",
                  }}
                  sortDirection={
                    sortConfig.key === "time" ? sortConfig.direction : false
                  }
                >
                  <TableSortLabel
                    active={sortConfig.key === "time"}
                    direction={
                      sortConfig.key === "time" ? sortConfig.direction : "asc"
                    }
                    onClick={() => handleSort("time")}
                    sx={{ color: "white !important" }}
                  >
                    Date
                  </TableSortLabel>
                </TableCell>

                <TableCell
                  sx={{
                    textAlign: "center",
                    fontWeight: "bold",
                    color: "white",
                    backgroundColor: "#000",
                    fontSize: { xs: "11px", sm: "14px", md: "15px" },
                    padding: "8px",
                  }}
                >
                  Unit ID
                </TableCell>

                <TableCell
                  sx={{
                    textAlign: "center",
                    display: { xs: "none", md: "table-cell" },
                    fontWeight: "bold",
                    color: "white",
                    backgroundColor: "#000",
                    fontSize: { xs: "11px", sm: "14px", md: "15px" },
                    padding: "8px",
                  }}
                >
                  Alarm
                </TableCell>

                <TableCell
                  sx={{
                    textAlign: "center",
                    display: { xs: "none", md: "table-cell" },
                    fontWeight: "bold",
                    color: "white",
                    backgroundColor: "#000",
                    fontSize: { xs: "11px", sm: "14px", md: "15px" },
                    padding: "8px",
                  }}
                >
                  Email List
                </TableCell>

                <TableCell
                  sx={{
                    textAlign: "center",
                    display: { xs: "none", md: "table-cell" },
                    fontWeight: "bold",
                    color: "white",
                    backgroundColor: "#000",
                    fontSize: { xs: "11px", sm: "14px", md: "15px" },
                    padding: "8px",
                  }}
                >
                  Phone List
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedNotifications.length > 0 ? (
                paginatedNotifications.map((notif, index) => (
                  <React.Fragment key={notif.id || index}>
                    <TableRow
                      hover
                      sx={{
                        backgroundColor:
                          index % 2 === 0 ? "#2f3136" : "#3a3c43",
                        "&:hover": {
                          backgroundColor: "#4a4d54",
                        },
                      }}
                    >
                      {/* Expand Button - Mobile Only */}
                      <TableCell
                        sx={{
                          display: { xs: "table-cell", md: "none" },
                          textAlign: "center",
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={() => toggleRowExpansion(notif.id || index)}
                          sx={{ color: "white" }}
                        >
                          {expandedRows[notif.id || index] ? (
                            <ExpandLessIcon />
                          ) : (
                            <ExpandMoreIcon />
                          )}
                        </IconButton>
                      </TableCell>

                      <TableCell
                        sx={{
                          textAlign: "center",
                          fontSize: { xs: "11px", sm: "14px", md: "15px" },
                        }}
                      >
                        {formatReadableDate(notif.time)}
                      </TableCell>

                      <TableCell
                        sx={{
                          textAlign: "center",
                          fontSize: { xs: "11px", sm: "14px", md: "15px" },
                        }}
                      >
                        {notif.unitId}
                      </TableCell>

                      <TableCell
                        sx={{
                          textAlign: "center",
                          display: { xs: "none", md: "table-cell" },
                          fontSize: { xs: "11px", sm: "14px", md: "15px" },
                        }}
                      >
                        {notif.notification}
                      </TableCell>

                      <TableCell
                        align="center"
                        sx={{
                          display: { xs: "none", md: "table-cell" },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          <Tooltip
                            title={
                              notif.emailsLength > 0
                                ? "View email list"
                                : "No emails"
                            }
                          >
                            <Box
                              onClick={
                                notif.emailsLength > 0
                                  ? () => openEmailInfo(notif)
                                  : undefined
                              }
                              sx={{
                                width: 28,
                                height: 28,
                                backgroundColor:
                                  notif.emailsLength > 0
                                    ? "#90caf9"
                                    : "grey.700",
                                borderRadius: "6px",
                                cursor:
                                  notif.emailsLength > 0
                                    ? "pointer"
                                    : "not-allowed",
                                transition: "all 0.2s ease",
                                color:
                                  notif.emailsLength > 0
                                    ? "#000000"
                                    : "grey.500",
                                fontSize: 13,
                                fontWeight: 600,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                opacity: notif.emailsLength > 0 ? 1 : 0.5,
                                "&:hover":
                                  notif.emailsLength > 0
                                    ? {
                                        backgroundColor: "primary.main",
                                        transform: "scale(1.1)",
                                      }
                                    : {},
                              }}
                            >
                              {notif.emailsLength}
                            </Box>
                          </Tooltip>
                        </Box>
                      </TableCell>

                      <TableCell
                        sx={{
                          display: { xs: "none", md: "table-cell" },
                        }}
                        align="center"
                      >
                          <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                        <Tooltip
                          title={
                            notif.recipientsLength > 0
                              ? "View phone list"
                              : "No phone numbers"
                          }
                        >
                          <Box
                            onClick={
                              notif.recipientsLength > 0
                                ? () => openPhoneInfo(notif)
                                : undefined
                            }
                            sx={{
                              width: 28,
                              height: 28,
                              backgroundColor:
                                notif.recipientsLength > 0
                                  ? "#90caf9"
                                  : "grey.700",
                              borderRadius: "6px",
                              cursor:
                                notif.recipientsLength > 0
                                  ? "pointer"
                                  : "not-allowed",
                              transition: "all 0.2s ease",
                              color:
                                notif.recipientsLength > 0
                                  ? "#000000"
                                  : "grey.500",
                              fontSize: 13,
                              fontWeight: 600,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              opacity: notif.recipientsLength > 0 ? 1 : 0.5,
                              "&:hover":
                                notif.recipientsLength > 0
                                  ? {
                                      backgroundColor: "primary.main",
                                      transform: "scale(1.1)",
                                    }
                                  : {},
                            }}
                          >
                            {notif.recipientsLength}
                          </Box>
                        </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>

                    {/* Mobile Collapsed Row */}
                    <TableRow sx={{ display: { xs: "table-row", md: "none" } }}>
                      <TableCell
                        style={{ paddingBottom: 0, paddingTop: 0 }}
                        colSpan={3}
                        sx={{
                          backgroundColor:
                            index % 2 === 0 ? "#2f3136" : "#3a3c43",
                        }}
                      >
                        <Collapse
                          in={expandedRows[notif.id || index]}
                          timeout="auto"
                          unmountOnExit
                        >
                          <Box sx={{ py: 2, px: 1 }}>
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1.5,
                              }}
                            >
                              <Box>
                                <Typography
                                  variant="caption"
                                  sx={{ color: "#999", fontWeight: "bold" }}
                                >
                                  Alarm:
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ color: "white" }}
                                >
                                  {notif.notification}
                                </Typography>
                              </Box>

                              <Box>
                                <Typography
                                  variant="caption"
                                  sx={{ color: "#999", fontWeight: "bold" }}
                                >
                                  Email List:
                                </Typography>
                                <Box sx={{ mt: 0.5 }}>
                                  <Tooltip
                                    title={
                                      notif.emailsLength > 0
                                        ? "View email list"
                                        : "No emails"
                                    }
                                  >
                                    <Box
                                      onClick={
                                        notif.emailsLength > 0
                                          ? () => openEmailInfo(notif)
                                          : undefined
                                      }
                                      sx={{
                                        width: 40,
                                        height: 40,
                                        backgroundColor:
                                          notif.emailsLength > 0
                                            ? "#90caf9"
                                            : "grey.700",
                                        borderRadius: "6px",
                                        cursor:
                                          notif.emailsLength > 0
                                            ? "pointer"
                                            : "not-allowed",
                                        transition: "all 0.2s ease",
                                        color:
                                          notif.emailsLength > 0
                                            ? "#000000"
                                            : "grey.500",
                                        fontSize: 16,
                                        fontWeight: 600,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        opacity:
                                          notif.emailsLength > 0 ? 1 : 0.5,
                                      }}
                                    >
                                      {notif.emailsLength}
                                    </Box>
                                  </Tooltip>
                                </Box>
                              </Box>

                              <Box>
                                <Typography
                                  variant="caption"
                                  sx={{ color: "#999", fontWeight: "bold" }}
                                >
                                  Phone List:
                                </Typography>
                                <Box sx={{ mt: 0.5 }}>
                                  <Tooltip
                                    title={
                                      notif.recipientsLength > 0
                                        ? "View phone list"
                                        : "No phone numbers"
                                    }
                                  >
                                    <Box
                                      onClick={
                                        notif.recipientsLength > 0
                                          ? () => openPhoneInfo(notif)
                                          : undefined
                                      }
                                      sx={{
                                        width: 40,
                                        height: 40,
                                        backgroundColor:
                                          notif.recipientsLength > 0
                                            ? "#90caf9"
                                            : "grey.700",
                                        borderRadius: "6px",
                                        cursor:
                                          notif.recipientsLength > 0
                                            ? "pointer"
                                            : "not-allowed",
                                        transition: "all 0.2s ease",
                                        color:
                                          notif.recipientsLength > 0
                                            ? "#000000"
                                            : "grey.500",
                                        fontSize: 16,
                                        fontWeight: 600,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        opacity:
                                          notif.recipientsLength > 0 ? 1 : 0.5,
                                      }}
                                    >
                                      {notif.recipientsLength}
                                    </Box>
                                  </Tooltip>
                                </Box>
                              </Box>
                            </Box>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    No alarm notifications found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={filteredNotifications.length}
          page={notificationsPage}
          onPageChange={handleNotificationsChangePage}
          rowsPerPage={notificationsRowsPerPage}
          onRowsPerPageChange={handleNotificationsChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          sx={{ mt: 1 }}
        />

        <Menu
          anchorEl={notificationExportMenuAnchor}
          open={Boolean(notificationExportMenuAnchor)}
          onClose={handleNotificationExportMenuClose}
        >
          <MenuItem onClick={copyToClipboard}>
            <ListItemIcon>
              <FileCopyIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Copy</ListItemText>
          </MenuItem>
          <MenuItem onClick={exportToCSV}>
            <ListItemIcon>
              <DescriptionIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>CSV</ListItemText>
          </MenuItem>
          <MenuItem onClick={exportToExcel}>
            <ListItemIcon>
              <TableViewIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Excel</ListItemText>
          </MenuItem>
          <MenuItem onClick={exportToPDF}>
            <ListItemIcon>
              <PictureAsPdfIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>PDF</ListItemText>
          </MenuItem>
        </Menu>
      </Paper>

      <EmailInfoNotification
        NotificationData={selectedNotification}
        open={showEmailInfo}
        onClose={() => setShowEmailInfo(false)}
        listType="email"
      />

      <RecipientInfoNotification
        NotificationData={selectedNotification}
        open={showPhoneInfo}
        onClose={() => setShowPhoneInfo(false)}
        listType="phone"
      />
    </Grid>
  );
}
